import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowDown, FiCheck, FiDownload, FiRefreshCw } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { API_BASE_URL } from '../api/axiosConfig';
const LOCATION_MOVE_TYPE = '하위층 통합';
const HORIZONTAL_MOVE_TYPE = '좌우 통합';
const parseLocationNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};
const getRecommendedFloor = (recommendedLocation) => {
    if (!recommendedLocation) {
        return '';
    }
    const [, floor = ''] = String(recommendedLocation).split('-');
    return floor;
};
const isHorizontalMove = (item) => {
    var _a;
    const currentFloor = String((_a = item.현재층) !== null && _a !== void 0 ? _a : '');
    const targetFloor = getRecommendedFloor(item.추천로케이션);
    return currentFloor !== '' && currentFloor === targetFloor;
};
const getRowKey = (item) => [
    item.id,
    item.상품코드,
    item.현재구역,
    item.현재층,
    item.현재렉,
    item.추천로케이션,
    item.이동수량,
    item.이동유형 || '',
].join('|');
const sortByLocation = (rows, order) => {
    return [...rows].sort((a, b) => {
        if (a.현재구역 !== b.현재구역) {
            const result = a.현재구역.localeCompare(b.현재구역);
            return order === 'asc' ? result : -result;
        }
        if (a.현재층 !== b.현재층) {
            const result = parseLocationNumber(a.현재층) - parseLocationNumber(b.현재층);
            return order === 'asc' ? result : -result;
        }
        const result = parseLocationNumber(a.현재렉) - parseLocationNumber(b.현재렉);
        return order === 'asc' ? result : -result;
    });
};
const StockMovementRecommendation = () => {
    const [data, setData] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');
    const sortedSelection = useMemo(() => data.map(getRowKey), [data]);
    const loadData = async () => {
        var _a;
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/stock/recommend-movement`);
            const rows = Array.isArray((_a = response.data) === null || _a === void 0 ? void 0 : _a.data) ? response.data.data : [];
            setData(sortByLocation(rows, sortOrder));
        }
        catch (loadError) {
            console.error('재고 이동 추천 데이터 조회 실패:', loadError);
            setError('재고 이동 추천 데이터를 불러오지 못했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        loadData();
    }, []);
    const toggleSortOrder = () => {
        const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(nextOrder);
        setData((prev) => sortByLocation(prev, nextOrder));
    };
    const handleSelectAll = (checked) => {
        setSelectedItems(checked ? sortedSelection : []);
    };
    const handleSelectItem = (item, checked) => {
        const rowKey = getRowKey(item);
        setSelectedItems((prev) => {
            if (checked) {
                return prev.includes(rowKey) ? prev : [...prev, rowKey];
            }
            return prev.filter((value) => value !== rowKey);
        });
    };
    const handleExcelDownload = () => {
        if (data.length === 0) {
            window.alert('다운로드할 재고 이동 추천 데이터가 없습니다.');
            return;
        }
        const rows = data.map((item, index) => ({
            순번: index + 1,
            ID: item.id,
            상품코드: item.상품코드,
            상품명: item.상품명,
            현재로케이션: `${item.현재구역}-${item.현재층}-${item.현재렉}`,
            현재구역: item.현재구역,
            현재층: item.현재층,
            현재렉: item.현재렉,
            재고수량: item.재고수량,
            이동수량: item.이동수량,
            상태: item.상태,
            추천로케이션: item.추천로케이션 || '-',
            이동유형: item.이동유형 || '',
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '재고이동추천');
        XLSX.writeFile(workbook, `재고이동추천_${new Date().toISOString().split('T')[0]}.xlsx`);
    };
    const processStockMovement = async (item) => {
        if (!item.추천로케이션) {
            return;
        }
        const [A, B, C] = item.추천로케이션.split('-');
        await axios.post(`${API_BASE_URL}/api/stock/move`, {
            sourceId: item.id,
            quantity: item.이동수량,
            targetLocation: { A, B, C },
        });
    };
    const handleMoveComplete = async () => {
        if (selectedItems.length === 0) {
            window.alert('이동할 항목을 선택해 주세요.');
            return;
        }
        if (!window.confirm(`선택한 ${selectedItems.length}개 항목을 이동 처리할까요?`)) {
            return;
        }
        setLoading(true);
        try {
            const selectedData = data.filter((item) => selectedItems.includes(getRowKey(item)));
            for (const item of selectedData) {
                await processStockMovement(item);
            }
            window.alert('재고 이동이 완료되었습니다.');
            setSelectedItems([]);
            await loadData();
        }
        catch (moveError) {
            console.error('재고 이동 처리 실패:', moveError);
            window.alert('재고 이동 중 오류가 발생했습니다.');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsx("div", { className: "container-fluid", children: _jsx("div", { className: "row", children: _jsx("div", { className: "col-12", children: _jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsxs("h5", { className: "mb-0", children: [_jsx(FiArrowDown, { className: "me-2" }), "\uC7AC\uACE0 \uC774\uB3D9 \uCD94\uCC9C"] }), _jsxs("div", { className: "d-flex gap-2", children: [_jsxs("button", { className: "btn btn-outline-success", onClick: handleExcelDownload, disabled: loading || data.length === 0, type: "button", children: [_jsx(FiDownload, { className: "me-1" }), "\uC5D1\uC140 \uB2E4\uC6B4\uB85C\uB4DC"] }), _jsxs("button", { className: "btn btn-outline-primary", onClick: loadData, disabled: loading, type: "button", children: [_jsx(FiRefreshCw, { className: `me-1 ${loading ? 'fa-spin' : ''}` }), "\uC0C8\uB85C\uACE0\uCE68"] }), _jsxs("button", { className: "btn btn-success", onClick: handleMoveComplete, disabled: loading || selectedItems.length === 0, type: "button", children: [_jsx(FiCheck, { className: "me-1" }), "\uC774\uB3D9\uC644\uB8CC (", selectedItems.length, ")"] })] })] }), _jsxs("div", { className: "card-body", children: [error && (_jsx("div", { className: "alert alert-danger", role: "alert", children: error })), loading ? (_jsxs("div", { className: "text-center py-4", children: [_jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }), _jsx("p", { className: "mt-2", children: "\uB370\uC774\uD130\uB97C \uBD88\uB7EC\uC624\uB294 \uC911..." })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "mb-3", children: _jsxs("small", { className: "text-muted", children: ["\uCD1D ", data.length, "\uAC1C \uD56D\uBAA9 | \uC120\uD0DD\uB41C \uD56D\uBAA9: ", selectedItems.length, "\uAC1C"] }) }), _jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-striped table-hover", children: [_jsx("thead", { className: "table-dark", children: _jsxs("tr", { children: [_jsx("th", { style: { width: '50px' }, children: _jsx("input", { type: "checkbox", className: "form-check-input", checked: data.length > 0 && selectedItems.length === data.length, onChange: (e) => handleSelectAll(e.target.checked) }) }), _jsx("th", { children: "ID" }), _jsx("th", { children: "\uC0C1\uD488\uCF54\uB4DC" }), _jsx("th", { children: "\uC0C1\uD488\uBA85" }), _jsx("th", { children: _jsxs("button", { className: "btn btn-link p-0 text-white text-decoration-none", onClick: toggleSortOrder, title: "\uAD6C\uC5ED \u2192 \uCE35 \u2192 \uB809 \uC21C\uC11C\uB85C \uC815\uB82C", type: "button", children: ["\uD604\uC7AC\uAD6C\uC5ED", _jsx("span", { className: "ms-1", children: sortOrder === 'asc' ? '▲' : '▼' })] }) }), _jsx("th", { children: "\uD604\uC7AC\uCE35" }), _jsx("th", { children: "\uD604\uC7AC\uB809" }), _jsx("th", { children: "\uC7AC\uACE0\uC218\uB7C9" }), _jsx("th", { children: "\uC774\uB3D9\uC218\uB7C9" }), _jsx("th", { children: "\uC0C1\uD0DC" }), _jsx("th", { children: "\uCD94\uCC9C\uB85C\uCF00\uC774\uC158" })] }) }), _jsx("tbody", { children: data.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 11, className: "text-center py-4", children: _jsxs("div", { className: "text-muted", children: [_jsx(FiArrowDown, { size: 48, className: "mb-3" }), _jsx("p", { children: "\uC774\uB3D9\uD560 \uC7AC\uACE0\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4." }), _jsx("small", { children: "\uC801\uC815 \uC801\uC7AC\uC218\uB7C9\uC744 \uB118\uC9C0 \uC54A\uB294 \uBC94\uC704\uC5D0\uC11C\uB9CC \uCD94\uCC9C\uB429\uB2C8\uB2E4." })] }) }) })) : (data.map((item) => {
                                                            const rowKey = getRowKey(item);
                                                            const isMergeMove = item.이동유형 === LOCATION_MOVE_TYPE ||
                                                                item.이동유형 === HORIZONTAL_MOVE_TYPE ||
                                                                isHorizontalMove(item);
                                                            return (_jsxs("tr", { children: [_jsx("td", { children: _jsx("input", { type: "checkbox", className: "form-check-input", checked: selectedItems.includes(rowKey), onChange: (e) => handleSelectItem(item, e.target.checked) }) }), _jsx("td", { children: item.id }), _jsx("td", { children: _jsx("code", { className: "text-primary", children: item.상품코드 }) }), _jsx("td", { className: "text-truncate", style: { maxWidth: '220px' }, children: item.상품명 }), _jsx("td", { children: _jsx("span", { className: "badge bg-secondary", children: item.현재구역 }) }), _jsx("td", { children: _jsx("span", { className: "badge bg-info", children: item.현재층 }) }), _jsx("td", { children: _jsx("span", { className: "badge bg-info", children: item.현재렉 }) }), _jsx("td", { children: _jsx("strong", { className: "text-success", children: Number(item.재고수량).toLocaleString() }) }), _jsx("td", { children: _jsx("strong", { className: "text-warning", children: Number(item.이동수량).toLocaleString() }) }), _jsx("td", { children: _jsx("span", { className: `badge ${item.상태 === '입고완료' ? 'bg-success' : 'bg-warning'}`, children: item.상태 }) }), _jsx("td", { children: item.추천로케이션 ? (_jsxs("div", { className: "d-flex align-items-center", children: [_jsxs("span", { className: "text-muted me-2", children: [item.현재구역, "-", item.현재층, "-", item.현재렉] }), isMergeMove ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "text-warning me-2", children: "\u2192" }), _jsx("span", { className: "badge bg-warning", children: item.추천로케이션 })] })) : (_jsxs(_Fragment, { children: [_jsx(FiArrowDown, { className: "text-primary me-2" }), _jsx("span", { className: "badge bg-success", children: item.추천로케이션 })] }))] })) : (_jsx("span", { className: "text-muted", children: "-" })) })] }, rowKey));
                                                        })) })] }) })] }))] })] }) }) }) }));
};
export default StockMovementRecommendation;
